
import { useState, useEffect, useRef } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import { Check, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";

type City = {
  id: number;
  name: string;
  display_name: string;
};

interface CitySearchProps {
  onSelect: (cityId: number) => void;
  defaultValue?: number;
  nextFieldId?: string;
}

export function CitySearch({ onSelect, defaultValue, nextFieldId }: CitySearchProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  
  const debouncedSearch = useDebounce(searchValue, 300);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current && 
        !inputRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch cities based on search input
  useEffect(() => {
    if (debouncedSearch.trim() === "") {
      setCities([]);
      setOpen(false);
      return;
    }

    const fetchCities = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `https://api.discoveryconciergeservices.com/api/v1/cities/search/?q=${encodeURIComponent(debouncedSearch)}`
        );
        
        if (!response.ok) {
          throw new Error("Failed to fetch cities");
        }
        
        // Parse the response and handle the results array
        const data = await response.json();
        if (data && data.results && Array.isArray(data.results)) {
          setCities(data.results);
        } else {
          setCities([]);
        }
        setOpen(true);
      } catch (error) {
        console.error("Error fetching cities:", error);
        setCities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCities();
  }, [debouncedSearch]);

  // Set default selected city if provided
  useEffect(() => {
    if (defaultValue && cities.length > 0) {
      const city = cities.find(city => city.id === defaultValue);
      if (city) {
        setSelectedCity(city);
        setSearchValue(city.display_name);
      }
    }
  }, [defaultValue, cities]);

  const handleSelect = (city: City) => {
    setSelectedCity(city);
    setSearchValue(city.display_name);
    setOpen(false);
    onSelect(city.id);
    
    // Clear the cities array to ensure dropdown content disappears
    setCities([]);
    
    // Focus on the next input field if provided
    if (nextFieldId) {
      setTimeout(() => {
        const nextField = document.getElementById(nextFieldId);
        if (nextField) {
          nextField.focus();
        }
      }, 100);
    }
    
    // Blur the current input to ensure dropdown stays closed
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  return (
    <div className="relative w-full">
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          placeholder="Enter City"
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            if (e.target.value.trim() === "") {
              setSelectedCity(null);
            }
          }}
          className="w-full bg-white border-gray-200 text-black"
          onFocus={() => {
            if (debouncedSearch.trim() !== "" && cities.length > 0) setOpen(true);
          }}
          onBlur={() => {
            // Small delay to allow click events on dropdown items to complete
            setTimeout(() => {
              if (!dropdownRef.current?.contains(document.activeElement)) {
                setOpen(false);
              }
            }, 150);
          }}
        />
        <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
      </div>

      {open && cities.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg border border-gray-200"
        >
          {loading ? (
            <div className="p-2">
              <div className="h-8 w-full mb-2 bg-gray-200 animate-pulse rounded"></div>
              <div className="h-8 w-full mb-2 bg-gray-200 animate-pulse rounded"></div>
              <div className="h-8 w-full bg-gray-200 animate-pulse rounded"></div>
            </div>
          ) : (
            <div className="max-h-60 overflow-auto">
              <ul>
                {cities.map((city) => (
                  <li
                    key={city.id}
                    onClick={() => handleSelect(city)}
                    className={cn(
                      "py-2 px-4 text-sm cursor-pointer hover:bg-gray-100 flex items-center text-black",
                      selectedCity?.id === city.id ? "bg-gray-100" : ""
                    )}
                  >
                    {selectedCity?.id === city.id && (
                      <Check className="h-4 w-4 mr-2 text-green-500" />
                    )}
                    <span>{city.display_name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
